import type { ApiMachineryGetById, ApiReceptionMachineryGetById } from '~/types'
import type { ApiMachineryPublicBaseSelect } from '~/layers/customer-module/types'

/**
 * One data column configs that are used on the app-side for data-displaying and on the server-side for PDF generation.
 *
 * `translate` is the translation function provided by `@nuxt/i18n`. We pass it in directly as the parent i18n-instances have slightly different types between app- and server-side.
 */
export default (translate: I18n['t']) => {
  const machineryBaseInformation: OneDataColumn<ApiMachineryGetById>[] = [
    {
      title: translate('common.field.id.name'),
      key: 'id',
    },
    {
      title: translate('common.field.status.name'),
      key: 'status',
      render: ({ status }) => translate(`machinery.status.${status}`),
    },
  ]

  const machineryType: OneDataColumn<ApiMachineryPublicBaseSelect['type']>[] = [
    {
      title: translate('common.field.type.name'),
      key: 'name',
    },
  ]

  const machineryPublicVehicleData: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('machinery.field.producerCompanyName.name'),
      key: 'producerCompanyName',
      hideIfNullish: true,
    },
    {
      title: translate('machinery.field.rubric.name'),
      key: 'machineryRubric',
      render: ({ machineryRubric }) => translate(`machinery.rubric.${machineryRubric}`),
      hideIfNullish: true,
    },
    {
      title: translate('common.field.type.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.type?.name,
      hideIfNullish: true,
    },
    {
      title: translate('machinery.field.yearBuilt.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.yearBuilt,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.product.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.product,
      hideIfNullish: true,
    },
    {
      title: translate('machinery.field.drive.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.drive ? translate(`machinery.drive.${receptionMachinery.drive.name}`) : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('common.field.weight.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.weight,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.operatingHours.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.operatingHours ? (receptionMachinery.operatingHours + 50).toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
  ]

  const machineryPublicDimensions: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('receptionMachinery.field.mast.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.mast ? translate(`machinery.mast.${receptionMachinery.mast}`) : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('machinery.field.liftingHeight.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.liftingHeightInMillimeters ? receptionMachinery.liftingHeightInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.overallHeight.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => {
        const heightInMillimeters = receptionMachinery?.mastOverallHeightInMillimeters ?? receptionMachinery?.overallHeightInMillimeters
        return heightInMillimeters ? heightInMillimeters.toLocaleString('de-DE') : undefined
      },
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.forkCarriageWidth.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.forkCarriageWidthInMillimeters ? receptionMachinery.forkCarriageWidthInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.fem.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.fem,
      hideIfNullish: true,
    },
  ]

  const machineryPublicMeasurements: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('receptionMachinery.field.lengthToForkface.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.lengthToForkfaceInMillimeters ? receptionMachinery.lengthToForkfaceInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.totalLength.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.totalLengthInMillimeters ? receptionMachinery.totalLengthInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.wheelbase.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.wheelbaseInMillimeters ? receptionMachinery.wheelbaseInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.overallWidth.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.overallWidthInMillimeters ? receptionMachinery.overallWidthInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.groundClearanceCenter.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.groundClearanceCenter,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.cabinHeight.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.cabinHeightInMillimeters ? receptionMachinery.cabinHeightInMillimeters.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
  ]

  const machineryPublicBatteryData: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('receptionMachinery.field.producerCompanyName.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.battery?.producerCompanyName,
      hideIfNullish: true,
    },
    {
      title: translate('common.field.type.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.battery?.typeName,
      hideIfNullish: true,
    },
    {
      title: translate('common.field.weight.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.battery?.weight ? receptionMachinery.battery.weight.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.field.volt.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.battery?.volt,
      hideIfNullish: true,
    },
    {
      title: translate('machinery.field.yearBuilt.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.battery?.yearBuilt,
      hideIfNullish: true,
    },
  ]

  const machineryPublicMotorData: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('receptionMachinery.motor.field.producerCompanyName.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.motor?.producerCompanyName,
      hideIfNullish: true,
    },
    {
      title: translate('receptionMachinery.motor.field.power.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.motor?.power ? receptionMachinery.motor.power.toLocaleString('de-DE') : undefined,
      hideIfNullish: true,
    },
    {
      title: translate('common.field.type.name'),
      key: 'receptionMachinery',
      render: ({ receptionMachinery }) => receptionMachinery?.motor?.typeName,
      hideIfNullish: true,
    },
  ]

  const receptionMachinerySpecialEquipment: OneDataColumn<ApiReceptionMachineryGetById | Exclude<ApiMachineryPublicBaseSelect['receptionMachinery'], null>>[] = [
    {
      title: translate('machinery.field.specialEquipment.name'),
      key: 'receptionMachinerySpecialEquipmentRelation',
      render: ({ receptionMachinerySpecialEquipmentRelation, customSpecialEquipment }) => {
        const specialEquipments = receptionMachinerySpecialEquipmentRelation?.receptionMachinerySpecialEquipments?.map(type => type.typeName).join(', ')
        if (!specialEquipments && !customSpecialEquipment) {
          return undefined
        }
        return `${specialEquipments}${customSpecialEquipment ? `${specialEquipments ? ', ' : ''}${customSpecialEquipment}` : ''}`
      },
      hideIfNullish: true,
    },
  ]

  const machineryEquipment: OneDataColumn<ApiMachineryPublicBaseSelect>[] = [
    {
      title: translate('machineryAccessory.name', { count: 2 }),
      key: 'marketingMachineryAccessoryDescription',
    },
    {
      title: translate('machinery.field.specialEquipment.name'),
      key: 'marketingSpecialEquipmentDescription',
    },
  ]

  return {
    machineryBaseInformation,
    machineryType,
    machineryPublicVehicleData,
    machineryPublicDimensions,
    machineryPublicMeasurements,
    machineryPublicBatteryData,
    machineryPublicMotorData,
    receptionMachinerySpecialEquipment,
    machineryEquipment,
  }
}
